import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import gsap from 'gsap';

const SectionContainer = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  overflow: hidden;
`;

const PortalCircle = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(0, 191, 255, 0.5), rgba(160, 32, 240, 0.5));
  box-shadow: 
    0 0 60px rgba(0, 191, 255, 0.8),
    0 0 100px rgba(160, 32, 240, 0.5);
  filter: blur(5px);
  
  &:before {
    content: '';
    position: absolute;
    top: -15px;
    left: -15px;
    right: -15px;
    bottom: -15px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    animation: rotatePortal 10s linear infinite;
  }
  
  @keyframes rotatePortal {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ContentContainer = styled(motion.div)`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: white;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-shadow: 0 0 20px rgba(160, 32, 240, 0.7);
`;

const Description = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 600px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const PortalRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &:nth-child(1) {
    width: 340px;
    height: 340px;
    animation: rotateRing 20s linear infinite;
  }
  
  &:nth-child(2) {
    width: 380px;
    height: 380px;
    animation: rotateRing 25s linear infinite reverse;
  }
  
  &:nth-child(3) {
    width: 420px;
    height: 420px;
    animation: rotateRing 30s linear infinite;
  }
  
  @keyframes rotateRing {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FloatingParticle = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.7);
`;

const PortalSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !portalRef.current || !particlesRef.current) return;

    const setHeight = () => {
      if (containerRef.current) {
        containerRef.current.style.height = `${window.innerHeight}px`;
      }
    };

    setHeight();
    window.addEventListener('resize', setHeight);

    // Create floating particles
    const createParticles = () => {
      const container = particlesRef.current;
      if (!container) return;
      
      container.innerHTML = '';
      
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.position = 'absolute';
        particle.style.width = `${Math.random() * 4 + 2}px`;
        particle.style.height = particle.style.width;
        particle.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
        particle.style.borderRadius = '50%';
        particle.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.7)';
        
        // Random positions around the portal
        const angle = Math.random() * Math.PI * 2;
        const distance = 150 + Math.random() * 150;
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        
        particle.style.left = `calc(50% + ${x}px)`;
        particle.style.top = `calc(50% + ${y}px)`;
        
        container.appendChild(particle);
        
        // Animate particles
        gsap.to(particle, {
          x: Math.random() * 80 - 40,
          y: Math.random() * 80 - 40,
          opacity: Math.random() * 0.7 + 0.3,
          duration: Math.random() * 10 + 5,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }
    };
    
    createParticles();
    
    // Portal pulse animation
    gsap.to(portalRef.current, {
      scale: 1.05,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    return () => {
      window.removeEventListener('resize', setHeight);
    };
  }, []);

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <SectionContainer ref={containerRef}>
      <div style={{ position: 'absolute', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
        <PortalCircle ref={portalRef} />
        <PortalRing />
        <PortalRing />
        <PortalRing />
        <div ref={particlesRef} style={{ position: 'absolute', width: '100%', height: '100%' }} />
      </div>

      <ContentContainer
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <Title variants={textVariants}>Enter The Void</Title>
        <Description variants={textVariants}>
          Join us on a journey through digital dimensions. Our innovative platform is opening portals to experiences never before imagined.
        </Description>
      </ContentContainer>
    </SectionContainer>
  );
};

export default PortalSection; 